// One form, two routes, either leg - the shape of DealForm, rendered from the column definitions
// the API serves instead of from fields written here.
//
// /deliveries/:leg/new has no identifier because none has been allocated: the first save persists
// the row, the server allocates the SD-*/CD-* id, and the history entry is replaced with the edit
// route. Every save after that carries the version that was read, so a concurrent edit is refused
// rather than overwritten. A derived column is shown on edit with its computed value, read-only;
// on create there is nothing yet to compute from.
//
// Attachments (TX-US-0011) ride along. On an existing row a dropped file uploads at once; on a new
// row there is no identifier to attach to, so the files wait in the form and go the moment the
// first save has created the row - before Save navigates anywhere, so a file that fails is
// reported on the form the BD is still looking at, and the row stands.

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import {
  createDelivery,
  deleteAttachment,
  getDelivery,
  listAttachments,
  updateDelivery,
} from '../api.js';
import { notify } from '../ui/notify.jsx';
import AttachmentUploader from './attachments/AttachmentUploader.jsx';
import useAttachmentUploads from './attachments/useAttachmentUploads.js';
import { controlFor, emptyDraft, fromRow, hasContent, toValues } from './deliveryForm.js';
import { recallView } from './legViews.js';
import useColumns from './useColumns.js';

export default function DeliveryForm({ mode }) {
  const { leg, deliveryId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyFrom = mode === 'create' ? searchParams.get('from') : null;
  const { legs, error: columnsError } = useColumns();
  const columns = legs?.[leg];

  const [draft, setDraft] = useState(null);
  const [derived, setDerived] = useState({});
  const [version, setVersion] = useState(0);
  // The row this form already holds: the create route becomes the edit route on first save while
  // this component stays mounted, and without this the effect would refetch what was just written
  // over anything typed since. The same hazard DealForm records.
  const loadedId = useRef(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [mine, setMine] = useState(mode === 'create');
  const [attachments, setAttachments] = useState([]);
  const uploads = useAttachmentUploads();

  useEffect(() => {
    if (!columns) return undefined;

    if (mode !== 'edit') {
      if (copyFrom) {
        let cancelled = false;
        getDelivery(leg, copyFrom)
          .then((row) => {
            if (!cancelled) setDraft(fromRow(columns, row));
          })
          .catch((err) => {
            if (!cancelled) {
              setError(err.status === 404 ? 'The source row does not exist.' : err.message);
              setDraft(emptyDraft(columns));
            }
          });
        return () => { cancelled = true; };
      }
      setDraft((current) => current ?? emptyDraft(columns));
      return undefined;
    }
    if (loadedId.current === deliveryId) return undefined;

    let cancelled = false;
    getDelivery(leg, deliveryId)
      .then((row) => {
        if (cancelled) return;
        loadedId.current = row.delivery_id;
        setDraft(fromRow(columns, row));
        setDerived(row.derived);
        setVersion(row.version);
        setMine(row.mine);
        return listAttachments(leg, deliveryId).then((listed) => {
          if (!cancelled) setAttachments(listed);
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.status === 404 ? 'That row does not exist.' : err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, leg, deliveryId, columns, copyFrom]);

  // On an existing row, whatever is dropped goes at once. The identifier is the loaded one so
  // that the row created a moment ago by Save (which drained the queue itself) is not drained
  // twice while the route is still catching up.
  const { pending, draining, drain } = uploads;
  useEffect(() => {
    if (mode !== 'edit' || pending === 0 || draining) return;
    drain(leg, deliveryId).then(({ uploaded }) => {
      if (uploaded.length > 0) setAttachments((current) => [...current, ...uploaded]);
    });
  }, [mode, leg, deliveryId, pending, draining, drain]);

  useEffect(() => {
    if (!dirty && uploads.pending === 0) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, uploads.pending]);

  const set = (key) => (value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setDirty(true);
  };

  const save = async (close) => {
    setBusy(true);
    setError(null);

    try {
      const values = toValues(draft);
      const row = mode === 'create'
        ? await createDelivery(leg, values)
        : await updateDelivery(leg, deliveryId, values, version);

      loadedId.current = row.delivery_id;
      setDerived(row.derived);
      setVersion(row.version);
      setDirty(false);
      setMine(true);

      // The files dropped before the row existed. Drained here, on this page, before any
      // navigation: a failure is then reported where it happened and the row stands (AC2).
      const { uploaded, failed } = await uploads.drain(leg, row.delivery_id);
      if (uploaded.length > 0) setAttachments((current) => [...current, ...uploaded]);

      notify(`${row.delivery_id} ${mode === 'create' ? 'created' : 'saved'}.`, close ? {
        action: {
          label: 'Open',
          onClick: () => navigate(`/deliveries/${leg}/${row.delivery_id}/edit`),
        },
      } : undefined);

      if (failed) {
        setError('The row is saved. One or more files did not upload - see the attachments.');
        if (mode === 'create') {
          navigate(`/deliveries/${leg}/${row.delivery_id}/edit`, { replace: true });
        }
      } else if (close) {
        navigate(`/deliveries/${leg}${recallView(leg)}`);
      } else if (mode === 'create') {
        navigate(`/deliveries/${leg}/${row.delivery_id}/edit`, { replace: true });
      }
    } catch (err) {
      if (err.status === 409) {
        setError(
          'Someone else changed this row while you were editing it. '
            + 'Reload to see their version before saving again.',
        );
      } else if (err.status === 403) {
        setError('Only the person who added a row may change it.');
      } else if (err.fields) {
        setError(Object.values(err.fields).join(' '));
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    save(false);
  };

  const onDeleteAttachment = async (attachment) => {
    setError(null);
    try {
      await deleteAttachment(leg, deliveryId, attachment.attachment_id);
      setAttachments((current) => current.filter((a) => a.attachment_id !== attachment.attachment_id));
    } catch (err) {
      setError(err.message);
    }
  };

  if (columnsError) return <p className="error">{columnsError}</p>;
  if (legs && !columns) return <p className="error">There is no {leg} tracker.</p>;
  if (error && !draft) return <p className="error">{error}</p>;
  if (!draft) return <p className="muted">Loading…</p>;

  const canSave = !busy && !uploads.draining && hasContent(draft);

  return (
    <form className="deal-form" onSubmit={onSubmit}>
      <header className="register-header">
        <h2>{mode === 'create' ? `New ${leg} row` : `Edit ${deliveryId}`}</h2>
      </header>

      {error && <p className="error">{error}</p>}

      <div className="delivery-fields">
        {columns.map((column) => (column.derived
          ? (mode === 'edit' && (
            <div className="field" key={column.key}>
              <span className="field-label">
                {column.label}
                {column.note && <> <abbr title={column.note}>*</abbr></>}
              </span>
              {/* Computed by the server from the values as saved, so it is current to the last
                  save rather than to the keystroke. */}
              <output className="derived">{derived[column.key] ?? ''}</output>
            </div>
          ))
          : (
            <label className="field" key={column.key} htmlFor={column.key}>
              <span className="field-label">{column.label}</span>
              <Control id={column.key} column={column} value={draft[column.key]} onChange={set(column.key)} />
            </label>
          )))}
      </div>

      {/* The drop zone is offered only where the API would accept the upload: the caller's own
          row, or the row about to be created. Hiding it is not authorisation - the API refuses
          the rest with 403 - it merely does not offer what would be refused. */}
      {mine && (
        <AttachmentUploader
          existing={attachments}
          items={uploads.items}
          draining={uploads.draining}
          onFiles={uploads.add}
          onRemove={uploads.drop}
          onDelete={mode === 'edit' ? onDeleteAttachment : null}
          creating={mode === 'create'}
        />
      )}

      <div className="form-actions">
        <button type="submit" disabled={!canSave}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={() => save(true)} disabled={!canSave}>
          Save and close
        </button>
        <Link to={`/deliveries/${leg}${recallView(leg)}`}>Back to the tracker</Link>
      </div>
    </form>
  );
}

// The control a column is asked for with - deliveryForm.controlFor says which, from the served
// definition alone. A choice offers a blank first: a person may not know yet.
//
// aria-label is set explicitly so that getByLabel(column.label, { exact: true }) resolves
// directly to the control, bypassing the wrapping label's full textContent (which includes all
// <option> strings for a <select> and would not match the column label exactly).
function Control({ id, column, value, onChange }) {
  const control = controlFor(column, value);
  if (control === 'select') {
    return (
      <select id={id} aria-label={column.label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="" />
        {column.options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    );
  }
  if (control === 'textarea') {
    return <textarea id={id} aria-label={column.label} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  return <input id={id} aria-label={column.label} type={control} value={value} onChange={(e) => onChange(e.target.value)} />;
}
